import { taskDisplayTitle } from "./task-display-title";
// PHASE_4 / B1-frontend — the shared task-inbox table + lifecycle wiring.
//
// ONE table (ADR-014 §2 no-copy): both Görevlerim (employee me-tasks) and
// Görevler (admin task inbox) render THIS component — the only difference is
// the route the server scopes (me-tasks = own / admin = broader tier) + the
// page chrome the host Content supplies. The per-row affordances are:
//   * the deep-link "go to source" (task.link into the owning surface),
//   * complete / dismiss / snooze (assignee-only — see isCallerAssignee below),
//   * a Details affordance that opens the TaskDetail drawer.
//
// Lifecycle errors (403 not-assignee / 409 terminal-state) are surfaced via an
// alert above the table that NAMES the failing task's title — we never silently
// swallow them, and we refetch on success so the list reflects the new state.
// No money.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { useTranslation } from "react-i18next";

import {
  Badge,
  Button,
  Card,
  DataTable,
  Empty,
  ErrorState,
  Loading,
  type DataTableColumn,
  type DataTableWindow,
} from "@novemai/components";
import { formatDate } from "@novemai/i18n-sdk";

import { errorMessage } from "@/api/errors";
import type { KeysetQueryResult } from "@/api/keyset";
import { PersonLink } from "@/components/person-link";
import { completeTask, dismissTask, type TaskRow } from "./api";
import { SnoozeModal } from "./snooze-modal";
import { TaskDetail } from "./task-detail";
import { TaskSourceLink } from "./task-source-link";
import { useMeIdentity, type MeIdentityState } from "./use-me-identity";
import {
  clampTaskTableScrollTop,
  getTaskTableFocusScrollTop,
  getTaskTableWindow,
  TASK_TABLE_HEADER_HEIGHT,
  TASK_TABLE_MIN_INLINE_SIZE,
  TASK_TABLE_ROW_HEIGHT,
  TASK_TABLE_VIEWPORT_HEIGHT,
} from "./task-table-window";

function statusTone(status: string): "success" | "info" | "warning" | "danger" | "neutral" {
  switch (status) {
    case "open":
    case "in_progress":
      return "info";
    case "completed":
      return "success";
    case "snoozed":
      return "warning";
    case "dismissed":
    case "expired":
      return "danger";
    default:
      return "neutral";
  }
}

function priorityTone(priority: string): "danger" | "warning" | "info" | "neutral" {
  switch (priority) {
    case "urgent":
      return "danger";
    case "high":
      return "warning";
    case "normal":
      return "info";
    default:
      return "neutral";
  }
}

/** spec-16 terminal states — no lifecycle action applies (the server 409s too). */
const TERMINAL_STATES = new Set(["completed", "dismissed", "expired"]);

/**
 * Whether the CALLER is the row's assignee — mirrors the server's isAssignee
 * (routes/tasks.ts) exactly: typed assignee_employee_id vs the caller's
 * employee_id first, then the generic assignee_id vs employee_id OR identity_id.
 *
 * The server treats complete/dismiss/snooze as the assignee's employee_self
 * SELF-ACTION (see the tasks lifecycle ALLOW_LIST note in
 * tooling/spec-tools/check-route-gating.mjs): rendering these actions to a
 * non-assignee — even a super-admin — GUARANTEES a 403. Do not "helpfully"
 * ungate this; whether an HR admin should be able to complete a subordinate's
 * task is an open product question (BUILD_PLAN §7), and until it is decided the
 * UI matches the server. Convention is HIDE, not disable (ScopeGate precedent:
 * unavailable actions render nothing). Default-closed: while /v1/me is still
 * loading both ids are null, so nothing matches and the actions stay hidden.
 */
function isCallerAssignee(row: TaskRow, me: MeIdentityState): boolean {
  if (row.assignee_employee_id !== null) {
    if (me.employeeId !== null && row.assignee_employee_id === me.employeeId) return true;
  }
  if (row.assignee_id !== null) {
    return (
      (me.employeeId !== null && row.assignee_id === me.employeeId)
      || (me.identityId !== null && row.assignee_id === me.identityId)
    );
  }
  return false;
}

/** HR timeout tasks close through the linked approval resolution, so a generic
 * task action cannot hide the review while leaving its approval blocked. */
function canUseLifecycleActions(row: TaskRow, me: MeIdentityState): boolean {
  return !TERMINAL_STATES.has(row.status) && isCallerAssignee(row, me)
    && !(row.source_spec === "org-modeling" && row.kind === "matrix_approval.hr_review_required");
}

/**
 * Whether the row names ANY assignee. Separates the two states the assignee
 * column used to collapse into one "—": a task nobody is assigned to, versus a
 * task whose assignee could not be named (tombstoned subject, or an identity-
 * keyed generic assignee_id the name join deliberately does not resolve).
 */
function hasAssignee(row: TaskRow): boolean {
  return row.assignee_employee_id !== null || row.assignee_id !== null;
}

// due_at is a timestamp, but its date part is a deadline rather than a
// wall-clock moment, so it goes through the UTC-pinned shared formatDate.

type TaskTableViewportStyle = CSSProperties & {
  readonly "--task-table-header-height": string;
  readonly "--task-table-min-inline-size": string;
  readonly "--task-table-row-height": string;
  readonly "--task-table-viewport-height": string;
};

// The CSS reads these variables for the sticky header and fixed row geometry;
// the window helper above uses the same constants, keeping one source of truth.
const TASK_TABLE_VIEWPORT_STYLE: TaskTableViewportStyle = {
  "--task-table-header-height": `${TASK_TABLE_HEADER_HEIGHT}px`,
  "--task-table-min-inline-size": `${TASK_TABLE_MIN_INLINE_SIZE}px`,
  "--task-table-row-height": `${TASK_TABLE_ROW_HEIGHT}px`,
  "--task-table-viewport-height": `${TASK_TABLE_VIEWPORT_HEIGHT}px`,
};

type TaskTableFocusEdge = "first" | "last";

interface PendingTaskTableFocus {
  readonly rowIndex: number;
  readonly edge: TaskTableFocusEdge;
}

const TASK_TABLE_FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

function taskTableRowAtIndex(
  viewport: HTMLDivElement,
  rowIndex: number,
): HTMLTableRowElement | null {
  return viewport.querySelector<HTMLTableRowElement>(
    `tr[data-slot="data-table-row"][data-row-index="${rowIndex}"]`,
  );
}

function taskTableRowIndex(row: HTMLTableRowElement): number | null {
  const rowIndex = Number(row.dataset.rowIndex);
  return Number.isInteger(rowIndex) && rowIndex >= 0 ? rowIndex : null;
}

function taskTableFocusableElements(row: HTMLTableRowElement): HTMLElement[] {
  return Array.from(row.querySelectorAll<HTMLElement>(TASK_TABLE_FOCUSABLE_SELECTOR)).filter(
    (element) => element.tabIndex >= 0 && element.closest('[aria-hidden="true"]') === null,
  );
}

function focusTaskTableRowEdge(row: HTMLTableRowElement, edge: TaskTableFocusEdge): boolean {
  const focusable = taskTableFocusableElements(row);
  const target = edge === "first" ? focusable[0] : focusable[focusable.length - 1];
  if (target === undefined) return false;
  target.focus();
  return true;
}

function TaskTableSpacer({
  height,
  colSpan,
}: {
  readonly height: number;
  readonly colSpan: number;
}): React.ReactElement {
  return (
    <tr data-slot="task-table-spacer" aria-hidden="true">
      <td
        colSpan={colSpan}
        style={{ border: 0, height: 0, lineHeight: 0, padding: 0 }}
      >
        <div style={{ height }} />
      </td>
    </tr>
  );
}

export interface TasksTableProps {
  readonly query: KeysetQueryResult<TaskRow>;
  /** ARIA caption + empty-state copy switch between me-tasks and admin. */
  readonly caption: string;
  readonly emptyTitle: string;
  readonly emptyDescription: string;
  /** Show the assignee column (admin view) vs hide it (me-tasks = all mine). */
  readonly showAssignee?: boolean;
  /**
   * Hands the drawer's open task to the host (me-tasks keeps it in the URL so
   * a task can be deep-linked). Omitted, the drawer state stays local.
   */
  readonly detail?: {
    readonly task: TaskRow | null;
    readonly onChange: (task: TaskRow | null) => void;
  };
}

export function TasksTable({
  query,
  caption,
  emptyTitle,
  emptyDescription,
  showAssignee = false,
  detail,
}: TasksTableProps): React.ReactElement {
  const { t, i18n } = useTranslation();
  const me = useMeIdentity();
  const [localDetail, setLocalDetail] = useState<TaskRow | null>(null);
  const detailFor = detail !== undefined ? detail.task : localDetail;
  const setDetailFor = detail !== undefined ? detail.onChange : setLocalDetail;
  const [snoozeFor, setSnoozeFor] = useState<TaskRow | null>(null);
  const pendingActions = useRef(new Set<string>());
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());
  // Title travels with the message so the banner can NAME the failing row —
  // on a long list an unanchored "access denied" above the table is unfindable.
  const [actionError, setActionError] = useState<{
    readonly taskTitle: string;
    readonly message: string;
  } | null>(null);
  const tableViewportRef = useRef<HTMLDivElement>(null);
  const emptyStateRef = useRef<HTMLDivElement>(null);
  const previousResultRef = useRef<{ readonly rowCount: number; readonly firstTaskId: string | null } | null>(null);
  const pendingTaskRowFocusRef = useRef<PendingTaskTableFocus | null>(null);
  const focusedTaskRowRef = useRef<HTMLTableRowElement | null>(null);
  const scrollTopRef = useRef(0);
  const [tableScrollTop, setTableScrollTop] = useState(0);
  const rowCount = query.items.length;
  const firstTaskId = query.items[0]?.task_id ?? null;

  async function runAction(
    task: TaskRow,
    action: (id: string) => Promise<TaskRow>,
  ): Promise<void> {
    // Guard before React renders disabled controls, including a reopened drawer.
    if (pendingActions.current.has(task.task_id)) return;
    pendingActions.current.add(task.task_id);
    setBusyIds(new Set(pendingActions.current));
    setActionError(null);
    try {
      await action(task.task_id);
      query.refetch();
    } catch (err) {
      // Honest surfacing: 403 (not-assignee) and 409 (terminal-state) reach here
      // as EnvelopeErrors — show the localized message, never swallow it. The
      // envelope carries only the generic auth.scope_forbidden code (no
      // task-specific reason), so the copy stays generic; the task title is
      // what anchors it to the row.
      setActionError({ taskTitle: taskDisplayTitle(task, t), message: errorMessage(err, t) });
    } finally {
      pendingActions.current.delete(task.task_id);
      setBusyIds(new Set(pendingActions.current));
    }
  }

  const columns: DataTableColumn<TaskRow>[] = [
    {
      id: "title",
      header: t("tasks.column_title"),
      cell: (row): React.ReactNode => (
        <button
          type="button"
          onClick={(): void => setDetailFor(row)}
          className="block max-w-[34ch] truncate text-left font-serif text-[14.5px] text-[var(--fg-primary)] hover:text-[var(--accent)] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
        >
          {taskDisplayTitle(row, t)}
        </button>
      ),
    },
    {
      id: "kind",
      header: t("tasks.column_kind"),
      // The kind vocabulary is open-ended (each source spec owns its kinds), so
      // known values resolve through tasks.kind.* and an unmapped one falls back
      // to the raw token — a last resort, not the norm. Not monospace: this is
      // a user-facing label, not an identifier.
      cell: (row): React.ReactNode => (
        <span className="whitespace-nowrap text-[12px] text-[var(--fg-muted)]">
          {t(`tasks.kind.${row.kind}`, { defaultValue: row.kind })}
        </span>
      ),
    },
    {
      id: "priority",
      header: t("tasks.column_priority"),
      cell: (row): React.ReactNode => (
        <Badge tone={priorityTone(row.priority)}>{t(`tasks.priority.${row.priority}`)}</Badge>
      ),
    },
    {
      id: "status",
      header: t("tasks.column_status"),
      cell: (row): React.ReactNode => (
        <Badge tone={statusTone(row.status)}>{t(`tasks.status.${row.status}`)}</Badge>
      ),
    },
    {
      id: "due",
      header: t("tasks.column_due"),
      cell: (row): React.ReactNode => (
        <span className="whitespace-nowrap text-[12.5px] text-[var(--fg-muted)]">
          {row.due_at !== null ? formatDate(i18n.language, row.due_at) : t("tasks.detail_none")}
        </span>
      ),
    },
  ];

  if (showAssignee) {
    columns.push({
      id: "assignee",
      header: t("tasks.column_assignee"),
      // Never render the raw id (or a hex fragment of it — SEM-5146). "you"
      // still wins over the name for the caller's own rows (it is the actionable
      // fact this column carries), then the server-joined name. The
      // unresolved-assignee placeholder is reached ONLY when there is no name to
      // show — it must not be confused with "unassigned", which is what the
      // pre-join cell was telling every reader.
      cell: (row): React.ReactNode => (
        // The link rides the employee id only. `assignee_id` can name a GROUP
        // (routes/tasks.ts), and a group has no person address; "unassigned"
        // and "you" are not references to a third party either.
        <PersonLink employeeId={row.assignee_employee_id}>
          <span className="whitespace-nowrap text-[12px] text-[var(--fg-secondary)]">
            {isCallerAssignee(row, me)
              ? t("tasks.assignee_you")
              : row.source_spec === "org-modeling" && row.kind === "matrix_approval.hr_review_required" && row.assignee_id === "role_hr_admin"
                ? t("tasks.assignee_hr_review")
              : row.assignee_employee_name !== null
                ? row.assignee_employee_name
                : hasAssignee(row)
                  ? t("tasks.assignee_unresolved")
                  : t("tasks.assignee_unassigned")}
          </span>
        </PersonLink>
      ),
    });
  }

  columns.push({
    id: "actions",
    header: t("tasks.column_actions"),
    align: "end",
    cell: (row): React.ReactNode => {
      const busy = busyIds.has(row.task_id);
      return (
        <div className="flex items-center justify-end gap-2 whitespace-nowrap">
          {row.link !== null && row.link !== "" && (
            <TaskSourceLink link={row.link} className="text-sm" />
          )}
          {canUseLifecycleActions(row, me) && (
            <>
              <Button
                variant="primary"
                size="sm"
                disabled={busy}
                onClick={(): void => void runAction(row, completeTask)}
              >
                {t("tasks.complete")}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={(): void => setSnoozeFor(row)}
              >
                {t("tasks.snooze")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={(): void => void runAction(row, dismissTask)}
              >
                {t("tasks.dismiss")}
              </Button>
            </>
          )}
        </div>
      );
    },
  });

  const requestVirtualRowFocus = useCallback((rowIndex: number, edge: TaskTableFocusEdge): boolean => {
    const viewport = tableViewportRef.current;
    if (viewport === null || rowIndex < 0 || rowIndex >= rowCount) return false;
    if (taskTableRowAtIndex(viewport, rowIndex) !== null) return false;

    pendingTaskRowFocusRef.current = { rowIndex, edge };
    const nextScrollTop = clampTaskTableScrollTop({
      rowCount,
      scrollTop: getTaskTableFocusScrollTop(rowIndex),
    });
    scrollTopRef.current = nextScrollTop;
    if (viewport.scrollTop !== nextScrollTop) viewport.scrollTop = nextScrollTop;
    setTableScrollTop((current) => current === nextScrollTop ? current : nextScrollTop);
    return true;
  }, [rowCount]);

  const onTaskTableKeyDownCapture = useCallback((event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (
      event.key !== "Tab"
      || event.altKey
      || event.ctrlKey
      || event.metaKey
    ) return;

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const currentRow = target.closest<HTMLTableRowElement>('tr[data-slot="data-table-row"]');
    if (currentRow === null || !event.currentTarget.contains(currentRow)) return;

    const focusable = taskTableFocusableElements(currentRow);
    const boundaryFocusable = event.shiftKey ? focusable[0] : focusable[focusable.length - 1];
    if (target !== boundaryFocusable) return;

    const currentRowIndex = taskTableRowIndex(currentRow);
    if (currentRowIndex === null) return;
    const targetRowIndex = currentRowIndex + (event.shiftKey ? -1 : 1);
    const targetEdge: TaskTableFocusEdge = event.shiftKey ? "last" : "first";

    if (requestVirtualRowFocus(targetRowIndex, targetEdge)) event.preventDefault();
  }, [requestVirtualRowFocus]);

  const onTaskTableFocusCapture = useCallback((event: React.FocusEvent<HTMLDivElement>): void => {
    const target = event.target;
    focusedTaskRowRef.current = target instanceof HTMLElement
      ? target.closest<HTMLTableRowElement>('tr[data-slot="data-table-row"]')
      : null;
  }, []);

  const onTaskTableBlurCapture = useCallback((event: React.FocusEvent<HTMLDivElement>): void => {
    const nextTarget = event.relatedTarget;
    if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) return;
    focusedTaskRowRef.current = null;
  }, []);

  const onTableScroll = useCallback((event: React.UIEvent<HTMLDivElement>): void => {
    const activeElement = document.activeElement;
    const activeRow = activeElement instanceof HTMLElement && event.currentTarget.contains(activeElement)
      ? activeElement.closest<HTMLTableRowElement>('tr[data-slot="data-table-row"]')
      : null;
    const clampedScrollTop = clampTaskTableScrollTop({
      rowCount,
      scrollTop: event.currentTarget.scrollTop,
    });
    const nextWindow = getTaskTableWindow({ rowCount, scrollTop: clampedScrollTop });
    const activeRowIndex = activeRow === null ? null : taskTableRowIndex(activeRow);
    // A wheel/scrollbar move can otherwise unmount the focused control and
    // leave the browser's active element at <body>. Keep a stable, labeled
    // scroll owner focused before React replaces that virtual slice.
    if (
      activeRowIndex !== null
      && (activeRowIndex < nextWindow.startIndex || activeRowIndex >= nextWindow.endIndex)
    ) event.currentTarget.focus();
    if (event.currentTarget.scrollTop !== clampedScrollTop) {
      event.currentTarget.scrollTop = clampedScrollTop;
    }
    scrollTopRef.current = clampedScrollTop;
    setTableScrollTop((current) => current === clampedScrollTop ? current : clampedScrollTop);
  }, [rowCount]);

  useLayoutEffect(() => {
    const pending = pendingTaskRowFocusRef.current;
    if (pending === null) return;

    const viewport = tableViewportRef.current;
    if (viewport === null) return;
    pendingTaskRowFocusRef.current = null;

    const targetRow = taskTableRowAtIndex(viewport, pending.rowIndex);
    if (targetRow === null || !focusTaskTableRowEdge(targetRow, pending.edge)) viewport.focus();
  }, [rowCount, tableScrollTop]);

  // Filter/query replacements can unmount a focused row without dispatching a
  // scroll event. Keep the concrete row node from focus capture so, after the
  // commit, we can tell a retained row from a replacement at the same index.
  useLayoutEffect(() => {
    const viewport = tableViewportRef.current;
    if (viewport === null) {
      const focusedRow = focusedTaskRowRef.current;
      focusedTaskRowRef.current = null;
      if (rowCount === 0 && focusedRow !== null) emptyStateRef.current?.focus();
      return;
    }

    const focusedRow = focusedTaskRowRef.current;
    if (focusedRow === null || viewport.contains(focusedRow)) return;

    focusedTaskRowRef.current = null;
    viewport.focus();
  }, [query.items, rowCount, tableScrollTop]);

  // A query reset or a mutation-induced result shrink must not leave the
  // viewport looking at a stale blank spacer. Appending a normal keyset page
  // preserves the reader's position; a new first row or a shorter result resets.
  useEffect(() => {
    const viewport = tableViewportRef.current;
    if (query.isLoading) {
      previousResultRef.current = null;
      scrollTopRef.current = 0;
      if (viewport !== null) viewport.scrollTop = 0;
      setTableScrollTop(0);
      return;
    }

    const previous = previousResultRef.current;
    const shouldReset = previous !== null
      && (rowCount < previous.rowCount || firstTaskId !== previous.firstTaskId);
    const nextScrollTop = shouldReset
      ? 0
      : clampTaskTableScrollTop({ rowCount, scrollTop: viewport?.scrollTop ?? scrollTopRef.current });

    scrollTopRef.current = nextScrollTop;
    if (viewport !== null && viewport.scrollTop !== nextScrollTop) {
      viewport.scrollTop = nextScrollTop;
    }
    setTableScrollTop((current) => current === nextScrollTop ? current : nextScrollTop);
    previousResultRef.current = { rowCount, firstTaskId };
  }, [firstTaskId, query.isLoading, rowCount]);

  const renderWindow = useCallback<DataTableWindow<TaskRow>>(({ rows, renderRow }) => {
    const window = getTaskTableWindow({ rowCount: rows.length, scrollTop: tableScrollTop });
    return (
      <>
        {window.topSpacerHeight > 0 && (
          <TaskTableSpacer
            key="task-table-top-spacer"
            height={window.topSpacerHeight}
            colSpan={columns.length}
          />
        )}
        {rows.slice(window.startIndex, window.endIndex).map((row, index) =>
          renderRow(row, window.startIndex + index),
        )}
        {window.bottomSpacerHeight > 0 && (
          <TaskTableSpacer
            key="task-table-bottom-spacer"
            height={window.bottomSpacerHeight}
            colSpan={columns.length}
          />
        )}
      </>
    );
  }, [columns.length, tableScrollTop]);

  if (query.isLoading) return <Loading label={t("tasks.loading")} rows={6} />;
  if (query.isError) {
    return (
      <ErrorState
        title={t("tasks.error_title")}
        description={errorMessage(query.error, t)}
      />
    );
  }

  return (
    <>
      {actionError !== null && (
        <p role="alert" className="mb-3 text-sm text-[var(--color-error)]">
          {t("tasks.action_error_for", { title: actionError.taskTitle })}{" "}
          {actionError.message}
        </p>
      )}
      {query.items.length > 0 ? (
        <Card className="overflow-hidden">
          <div
            ref={tableViewportRef}
            role="region"
            aria-label={caption}
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a labeled overflow region needs a keyboard scroll/focus fallback target
            tabIndex={0}
            data-slot="task-table-scroll-region"
            onScroll={onTableScroll}
            onFocusCapture={onTaskTableFocusCapture}
            onBlurCapture={onTaskTableBlurCapture}
            onKeyDownCapture={onTaskTableKeyDownCapture}
            className="task-table-scroll-region"
            style={TASK_TABLE_VIEWPORT_STYLE}
          >
            <DataTable<TaskRow>
              caption={caption}
              ariaRowCount={query.hasNextPage ? -1 : rowCount + 1}
              columns={columns}
              rows={query.items}
              rowKey={(row): string => row.task_id}
              renderWindow={renderWindow}
            />
          </div>
          {query.hasNextPage && (
            <div data-slot="data-table-load-more">
              <button
                type="button"
                onClick={query.fetchNextPage}
                disabled={query.isFetchingNextPage}
                aria-busy={query.isFetchingNextPage || undefined}
                className="nai-data-table-load-more focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]"
              >
                {t("tasks.load_more")}
              </button>
            </div>
          )}
        </Card>
      ) : (
        <Empty
          ref={emptyStateRef}
          role="region"
          aria-label={emptyTitle}
          tabIndex={-1}
          className="task-table-empty-state"
          title={emptyTitle}
          description={emptyDescription}
        />
      )}

      {detailFor !== null && (
        <TaskDetail
          task={detailFor}
          open={detailFor !== null}
          onClose={(): void => setDetailFor(null)}
          footer={
            // Same gate as the row actions — the drawer must not resurface
            // buttons the table hid (they would 403 identically).
            canUseLifecycleActions(detailFor, me) ? (
              <div className="flex items-center gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={busyIds.has(detailFor.task_id)}
                  onClick={(): void => {
                    void runAction(detailFor, completeTask);
                    setDetailFor(null);
                  }}
                >
                  {t("tasks.complete")}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busyIds.has(detailFor.task_id)}
                  onClick={(): void => {
                    setSnoozeFor(detailFor);
                    setDetailFor(null);
                  }}
                >
                  {t("tasks.snooze")}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busyIds.has(detailFor.task_id)}
                  onClick={(): void => {
                    void runAction(detailFor, dismissTask);
                    setDetailFor(null);
                  }}
                >
                  {t("tasks.dismiss")}
                </Button>
              </div>
            ) : undefined
          }
        />
      )}

      {snoozeFor !== null && (
        <SnoozeModal
          task={snoozeFor}
          open={snoozeFor !== null}
          onClose={(): void => setSnoozeFor(null)}
          onSnoozed={query.refetch}
        />
      )}
    </>
  );
}
